
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Container,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import PublicNavbar from "../Components/PublicNavbar";
import TrackingRouteProgress from "./SSSAuto/Delivery/TrackOrder/TrackingRouteProgress";
import TrackingDriverSummary from "./SSSAuto/Delivery/TrackOrder/TrackingDriverSummary";
import PublicTrackingSearchSection from "./SSSAuto/Delivery/TrackOrder/PublicTrackingSearchSection";
import ScrollToTopButton from "./SSSAuto/Delivery/TrackOrder/ScrollToTopButton";
import httpclient from "../Utils";

// Import icons
import packageIcon from "../Assets/Icons/package-icon.png";
import calendarIcon from "../Assets/Icons/calendar-icon.png";
import calendarCheckIcon from "../Assets/Icons/calendar-check.png";
import userIcon from "../Assets/Icons/user-icon.png";
import InvoiceIcon from "../Assets/Icons/today-new-orders.png";

// Custom color mapping for statuses
const getStatusColor = (status) => {
  const statusMap = {
    'All': { bgcolor: '#9E9E9E', color: '#FFFFFF' },
    'Waiting for Delivery': { bgcolor: '#9E9E9E', color: '#FFFFFF' },
    'Waiting For Delivery': { bgcolor: '#9E9E9E', color: '#FFFFFF' },
    'In Queue': { bgcolor: '#1E3A8A', color: '#FFFFFF' },
    'In Progress': { bgcolor: '#2196F3', color: '#FFFFFF' },
    'Inprogress': { bgcolor: '#2196F3', color: '#FFFFFF' },
    'Paused': { bgcolor: '#FF9800', color: '#FFFFFF' },
    'Loaded': { bgcolor: '#00BCD4', color: '#FFFFFF' },
    'In Transit': { bgcolor: '#3F51B5', color: '#FFFFFF' },
    'InTransit': { bgcolor: '#3F51B5', color: '#FFFFFF' },
    'Drop To Depot': { bgcolor: '#04384d', color: '#FFFFFF' },
    'Drop to Depot': { bgcolor: '#04384d', color: '#FFFFFF' },
    'Delivered': { bgcolor: '#4CAF50', color: '#FFFFFF' },
    'Unloaded': { bgcolor: '#9C27B0', color: '#FFFFFF' },
    'Canceled': { bgcolor: '#F44336', color: '#FFFFFF' },
    'Cancelled': { bgcolor: '#F44336', color: '#FFFFFF' },
  };

  const normalizedStatus = status?.toString().trim() || '';
  
  if (statusMap[normalizedStatus]) {
    return statusMap[normalizedStatus];
  }
  
  const lowerStatus = normalizedStatus.toLowerCase();
  for (const [key, value] of Object.entries(statusMap)) {
    if (key.toLowerCase() === lowerStatus) {
      return value;
    }
  }
  
  if (lowerStatus.includes('waiting') || lowerStatus.includes('pending')) {
    return statusMap['Waiting for Delivery'];
  }
  if (lowerStatus.includes('queue')) {
    return statusMap['In Queue'];
  }
  if (lowerStatus.includes('progress')) {
    return statusMap['In Progress'];
  }
  if (lowerStatus.includes('paus')) {
    return statusMap['Paused'];
  }
  if (lowerStatus.includes('load')) {
    return statusMap['Loaded'];
  }
  if (lowerStatus.includes('transit')) {
    return statusMap['In Transit'];
  }
  if (lowerStatus.includes('depot')) {
    return statusMap['Drop To Depot'];
  }
  if (lowerStatus.includes('deliver')) {
    return statusMap['Delivered'];
  }
  if (lowerStatus.includes('unload')) {
    return statusMap['Unloaded'];
  }
  if (lowerStatus.includes('cancel')) {
    return statusMap['Canceled'];
  }
  
  return { bgcolor: '#9E9E9E', color: '#FFFFFF' };
};

// Map status for display
const mapStatus = (status) => {
  const statusMap = {
    'inQueue': 'In Queue',
    'inProgress': 'In Progress',
    'inprogress': 'In Progress',
    'paused': 'Paused',
    'loaded': 'Loaded',
    'inTransit': 'In Transit',
    'dropToDepot': 'Drop To Depot',
    'drop to depot': 'Drop To Depot',
    'delivered': 'Delivered',
    'unloaded': 'Unloaded',
    'canceled': 'Canceled',
    'cancelled': 'Canceled',
  };
  
  const normalizedStatus = status?.toString().toLowerCase().trim() || '';
  return statusMap[normalizedStatus] || status || 'Unknown';
};

// Summary Item Component
const SummaryItem = ({ icon, label, value, index, total }) => {
  const isLastItem = index === total - 1;
  const isLastRowSm = index >= total - (total % 2 || 2);
  const isLastRowMd = index >= total - (total % 3 || 3);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        px: { xs: 1, sm: 1.5 },
        py: { xs: 1, sm: 1.25 },
        borderRight: {
          xs: "none",
          sm: !isLastItem && index % 2 === 0 ? "1px solid #E5E7EB" : "none",
          md: !isLastItem && index % 3 !== 2 ? "1px solid #E5E7EB" : "none",
        },
        borderBottom: {
          xs: !isLastItem ? "1px solid #E5E7EB" : "none",
          sm: !isLastRowSm ? "1px solid #E5E7EB" : "none",
          md: !isLastRowMd ? "1px solid #E5E7EB" : "none",
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 18, sm: 20 },
          height: { xs: 18, sm: 20 },
          mt: 0.15,
          borderRadius: "6px",
          backgroundColor: "#F3F4F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          border: "1px solid #E4E7EC",
        }}
      >
        <Box
          component="img"
          src={icon}
          alt={label}
          sx={{
            width: { xs: 12, sm: 14 },
            height: { xs: 12, sm: 14 },
            opacity: 1,
            filter: "grayscale(1) brightness(0.72) contrast(0.95)",
          }}
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: { xs: "10px", sm: "11px" },
            lineHeight: 1.2,
            color: "#6B7280",
            mb: 0.35,
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            lineHeight: 1.25,
            color: "#111827",
            fontWeight: 600,
            wordBreak: "break-word",
          }}
        >
          {value || "-"}
        </Typography>
      </Box>
    </Box>
  );
};

// Customer Details Card Component
const CustomerDetailsCard = ({ customerDetails }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const labelStyle = {
    fontSize: { xs: "10px", sm: "11px" },
    lineHeight: 1.2,
    color: "#6B7280",
    mb: 0.35,
    fontWeight: 500,
  };

  const valueStyle = {
    fontSize: { xs: "12px", sm: "13px" },
    lineHeight: 1.25,
    color: "#111827",
    fontWeight: 600,
    wordBreak: "break-word",
  };

  return (
    <Box
      sx={{
        mt: 1.25,
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.65,
          px: { xs: 1.5, sm: 1.5 },
          py: { xs: 1, sm: 1.1 },
        }}
      >
        <PersonOutlineIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: "#374151" }} />
        <Typography
          sx={{
            fontSize: { xs: "14px", sm: "15px" },
            lineHeight: 1.2,
            color: "#111827",
            fontWeight: 600,
          }}
        >
          Customer Details
        </Typography>
      </Box>

      <Divider sx={{ borderColor: "#E5E7EB" }} />

      <Box
        sx={{
          px: { xs: 1.5, sm: 1.5 },
          py: { xs: 1.25, sm: 1.25 },
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          },
          gap: { xs: 1, sm: 1.25 },
        }}
      >
        <Box>
          <Typography sx={labelStyle}>Name</Typography>
          <Typography sx={valueStyle}>{customerDetails.customer_name || "-"}</Typography>
        </Box>
        <Box>
          <Typography sx={labelStyle}>Code</Typography>
          <Typography sx={valueStyle}>{customerDetails.customer_code || "-"}</Typography>
        </Box>
        <Box>
          <Typography sx={labelStyle}>Phone</Typography>
          <Typography sx={valueStyle}>{customerDetails.customer_phone || "-"}</Typography>
        </Box>
        <Box>
          <Typography sx={labelStyle}>Email</Typography>
          <Typography sx={valueStyle}>{customerDetails.customer_email || "-"}</Typography>
        </Box>
        <Box sx={{ gridColumn: { xs: "auto", sm: "span 2", md: "span 2" } }}>
          <Typography sx={labelStyle}>Address</Typography>
          <Typography sx={valueStyle}>{customerDetails.customer_address || "-"}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Order Information Component
const OrderInformation = ({ orderDetails }) => {
  const summaryFields = [
    {
      label: "Invoice No",
      value: orderDetails?.invoice_number,
      icon: InvoiceIcon,
    },
    {
      label: "Invoice Date",
      value: orderDetails?.invoice_date,
      icon: calendarIcon,
    },
    {
      label: "Job No",
      value: orderDetails?.job_id,
      icon: packageIcon,
    },
    {
      label: "Order Date",
      value: orderDetails?.order_date,
      icon: calendarIcon,
    },
    {
      label: "Delivery Date",
      value: orderDetails?.delivery_date,
      icon: calendarCheckIcon,
    },
    {
      label: "Driver",
      value: orderDetails?.assigned_driver,
      icon: userIcon,
    },
  ];

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {summaryFields.map((item, index) => (
          <SummaryItem
            key={item.label}
            icon={item.icon}
            label={item.label}
            value={item.value}
            index={index}
            total={summaryFields.length}
          />
        ))}
      </Box>

      <CustomerDetailsCard customerDetails={orderDetails} />
    </Box>
  );
};

const PublicTracking = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobId, setJobId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(0);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // File viewer states
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [viewerFiles, setViewerFiles] = useState([]);
  const [viewerTitle, setViewerTitle] = useState("");
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [viewerSnackbar, setViewerSnackbar] = useState({ open: false, message: "" });

  // Theme and breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Handle URL params for auto-search
  useEffect(() => {
    const jobIdFromUrl = searchParams.get("jobId");
    if (jobIdFromUrl) {
      setJobId(jobIdFromUrl);
      handleSearchWithId(jobIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // If the user clears both the invoice/job input and the driver selection,
  // clear any previously fetched results instead of leaving stale data shown.
  useEffect(() => {
    if (!jobId && !driverId) {
      setOrderData(null);
      setError("");
      setSearched(false);
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, driverId]);

  const handleSearchWithId = async (id) => {
    if (!id.trim()) {
      setError("Please enter a valid Job ID");
      return;
    }

    setLoading(true);
    setError("");
    setSearched(true);

    try {
      const API_URL = `delivery/track-job-status/${id.trim()}`;
      console.log("📡 Calling API:", API_URL);

      const res = await httpclient.get(API_URL);
      console.log("✅ API Response:", res.data);

      if (res.data.status === 200) {
        const rawData = res.data.data;

        // Build progress states from history
        const progressStatesWithDates = {};
        const progressStates = [];

        if (rawData.order_delivery_status_history && Array.isArray(rawData.order_delivery_status_history)) {
          rawData.order_delivery_status_history.forEach(history => {
            const mapped = mapStatus(history.status);
            progressStatesWithDates[mapped] = history.date_time;
            progressStates.push(mapped);
          });
        }

        const currentStatus = rawData.delivery_status || rawData.order_current_status_message || "Pending";

        const transformedData = {
          ...rawData,
          currentStatus: mapStatus(currentStatus),
          progressStatesWithDates,
          progressStates,
          hasPaused: rawData.order_delivery_status_history?.some(h =>
            h.status?.toLowerCase() === 'paused'
          ),
          hasUnloaded: rawData.order_delivery_status_history?.some(h =>
            h.status?.toLowerCase() === 'unload' || h.status?.toLowerCase() === 'unloaded'
          ),
          orderLines: rawData.orderLines || [],
          order_delivery_status_history: rawData.order_delivery_status_history || [],
        };

        setOrderData(transformedData);
        setSelectedHistoryIndex(0);
        setError("");
      } else {
        setError(res.data.message || "Order not found.");
        setOrderData(null);
      }
    } catch (err) {
      console.error("❌ API Error:", err);
      if (err.response?.status === 404) {
        setError(`Job "${id}" not found. Please check the Job ID and try again.`);
      } else if (err.response?.status === 500) {
        setError("Server error. Please try again later.");
      } else if (err.code === 'ECONNABORTED') {
        setError("Request timed out. Please try again.");
      } else {
        setError(err.response?.data?.message || "Failed to fetch order details. Please try again.");
      }
      setOrderData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = { jobId: jobId };
    if (driverId) {
      params.search = "driver";
    }
    setSearchParams(params);
    handleSearchWithId(jobId);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClearOrderId = () => {
    setJobId("");
    setError("");
    setSearched(false);
    setOrderData(null);
    setSearchParams({});
  };

  // File viewer handlers
  const getFilePath = (file) => {
    if (typeof file === "string") return file;
    return file?.file_path || "";
  };

  const getFileId = (file) => {
    if (!file || typeof file === "string") return undefined;
    return file.file_id || file.id || file.fileId;
  };

  const handleOpenFileViewer = (files, title) => {
    const validFiles = (files || []).filter((file) => Boolean(getFilePath(file)));
    if (validFiles.length === 0) {
      return;
    }
    setViewerFiles(validFiles);
    setViewerTitle(title);
    setSelectedFileIndex(0);
    setFileViewerOpen(true);
  };

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setViewerFiles([]);
    setViewerTitle("");
    setSelectedFileIndex(0);
  };

  const handleCloseViewerSnackbar = () => {
    setViewerSnackbar({ open: false, message: "" });
  };

  // Clipboard image-copy only reliably supports image/png across browsers.
  // Signature files happen to already be PNG, but other files can come back
  // as jpeg/octet-stream etc, which ClipboardItem rejects outright. Normalize
  // everything to PNG via canvas before writing to the clipboard.
  const convertBlobToPng = (blob) => {
    return new Promise((resolve, reject) => {
      const objectUrl = window.URL.createObjectURL(blob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((pngBlob) => {
          window.URL.revokeObjectURL(objectUrl);
          if (pngBlob) {
            resolve(pngBlob);
          } else {
            reject(new Error("Failed to convert image to PNG"));
          }
        }, "image/png");
      };

      img.onerror = (err) => {
        window.URL.revokeObjectURL(objectUrl);
        reject(err);
      };

      img.src = objectUrl;
    });
  };

  const handleCopyImage = async () => {
    const currentFile = viewerFiles[selectedFileIndex];
    const fileId = getFileId(currentFile);
    const src = getFilePath(currentFile);
    if (!fileId && !src) return;

    try {
      const response = await httpclient.get(`/view-file/${fileId}`, {
        responseType: "blob",
      });
      const pngBlob = await convertBlobToPng(response.data);
      await navigator.clipboard.write([
        new window.ClipboardItem({ "image/png": pngBlob }),
      ]);
      setViewerSnackbar({ open: true, message: "Image copied to clipboard" });
    } catch (error) {
      // Fallback: copy the image URL if copying the image itself isn't supported
      try {
        await navigator.clipboard.writeText(src);
        setViewerSnackbar({ open: true, message: "Image link copied to clipboard" });
      } catch (fallbackError) {
        setViewerSnackbar({ open: true, message: "Unable to copy image" });
      }
    }
  };

  const handleDownloadImage = async () => {
    const currentFile = viewerFiles[selectedFileIndex];
    const fileId = getFileId(currentFile);
    const src = getFilePath(currentFile);
    if (!fileId && !src) return;

    try {
      const response = await httpclient.get(`/download-file/${fileId}`, {
        responseType: "blob",
      });
      const objectUrl = window.URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = currentFile?.file_name || "signature-file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      // Fallback: open the file in a new tab if direct download fails (e.g. CORS)
      window.open(src, "_blank", "noopener,noreferrer");
    }
  };

  const handlePrevFile = () => {
    setSelectedFileIndex((prev) =>
      prev === 0 ? viewerFiles.length - 1 : prev - 1
    );
  };

  const handleNextFile = () => {
    setSelectedFileIndex((prev) =>
      prev === viewerFiles.length - 1 ? 0 : prev + 1
    );
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }
    return parsedDate.toLocaleString("en-AU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const statusColors = getStatusColor(orderData?.delivery_status);
  const deliveryStatusHistory = orderData?.order_delivery_status_history || [];
  // Driver-specific UI (summary cards, route progress, drop column) is
  // controlled by the API's own is_driver_search flag rather than whether a
  // driverId happens to be set locally, since the flag reflects how the
  // backend actually resolved the search.
  const isDriverSearch = orderData?.is_driver_search === 1;

  // Each row in the status history can belong to a different invoice/job
  // (different customer + order lines) than the top-level searched job. When
  // a row is selected, merge that row's fields over the top-level order so
  // the Customer Details / Items in Order sections reflect the selected
  // invoice while falling back to the searched job's data for fields the
  // history row doesn't carry (address/email/order dates, etc).
  const selectedHistory = deliveryStatusHistory[selectedHistoryIndex] || null;
  const selectedDetails = orderData
    ? {
        ...orderData,
        customer_name: selectedHistory?.customer_name || orderData.customer_name,
        customer_code: selectedHistory?.customer_code || orderData.customer_code,
        customer_phone: selectedHistory?.customer_phone || orderData.customer_phone,
        customer_address: selectedHistory?.customer_address || orderData.customer_address,
        customer_email: selectedHistory?.customer_email || orderData.customer_email,
        invoice_number: selectedHistory?.invoice_number || orderData.invoice_number,
        invoice_date: selectedHistory?.invoice_date || orderData.invoice_date,
        job_id: selectedHistory?.job_no || selectedHistory?.newSystemOrderID || orderData.job_id,
        assigned_driver: selectedHistory?.driver || orderData.assigned_driver,
        orderLines: selectedHistory?.order_lines?.length
          ? selectedHistory.order_lines
          : selectedHistory
          ? []
          : orderData.orderLines,
      }
    : null;

  // Determine scroll height based on device and number of statuses
  const getTableMaxHeight = () => {
    const statusCount = deliveryStatusHistory.length;
    
    if (isMobile) {
      // Mobile: scroll if more than 3 statuses
      return statusCount > 3 ? '300px' : 'auto';
    } else if (isTablet) {
      // Tablet: scroll if more than 4 statuses
      return statusCount > 4 ? '400px' : 'auto';
    } else {
      // Desktop: scroll if more than 5 statuses
      return statusCount > 5 ? '500px' : 'auto';
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
      <PublicNavbar showTrackingTitle />

      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <PublicTrackingSearchSection
          orderId={jobId}
          setOrderId={setJobId}
          handleSearch={handleSearch}
          handleKeyPress={handleKeyPress}
          handleClearOrderId={handleClearOrderId}
          loading={loading}
          error={error}
          searched={searched}
          driverId={driverId}
          setDriverId={setDriverId}
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {orderData && !loading && (
          <Box sx={{ mt: { xs: 2, sm: 3, md: 4 }, animation: "fadeIn 0.6s ease" }}>
            <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>

            <Paper
              elevation={0}
              sx={{
                borderRadius: { xs: "6px", sm: "8px" },
                border: "1px solid #E5E7EB",
                overflow: "hidden",
                backgroundColor: "#FFFFFF",
              }}
            >
              {/* Header with Status Chip */}
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2, md: 2.5 },
                  py: { xs: 1.5, sm: 1.75, md: 2 },
                  borderBottom: "1px solid #E5E7EB",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexWrap: "wrap",
                  gap: 1,
                  backgroundColor: "#FAFAFA",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexWrap: "wrap" }}>
                  {isDriverSearch ? (
                    // Drops/References/Lines chip replaced by the Driver /
                    // Stops Completed / Orders Delivered summary cards below.
                    // {(() => {
                    //   const summary = orderData?.driver_summary || {};
                    //   const drops = summary.total_drops ?? 0;
                    //   const references = summary.total_references ?? 0;
                    //   const lines = summary.total_lines ?? 0;
                    //   const parts = [];
                    //   if (drops) {
                    //     parts.push(
                    //       `${drops} Drop${drops === 1 ? "" : "s"}/Stop${drops === 1 ? "" : "s"}`
                    //     );
                    //   }
                    //   parts.push(`${references} Reference${references === 1 ? "" : "s"}`);
                    //   parts.push(`${lines} Line${lines === 1 ? "" : "s"}`);
                    //   return (
                    //     <Chip
                    //       label={parts.join(", ")}
                    //       sx={{
                    //         backgroundColor: "#48505E",
                    //         color: "#fff",
                    //         fontWeight: 600,
                    //         height: { xs: 26, sm: 32 },
                    //         fontSize: { xs: "11px", sm: "13px" },
                    //       }}
                    //     />
                    //   );
                    // })()}
                    null
                  ) : (
                    <>
                      <Typography variant="h6" sx={{
                        fontWeight: 600,
                        fontSize: { xs: "14px", sm: "15px", md: "16px" },
                        color: "#111827"
                      }}>
                        Job Details
                      </Typography>
                      <Chip
                        size="small"
                        label={mapStatus(orderData?.delivery_status) || "Unknown"}
                        sx={{
                          backgroundColor: orderData?.delivery_status_color || getStatusColor(orderData?.delivery_status).bgcolor,
                          color: orderData?.delivery_status_color ? "#fff" : getStatusColor(orderData?.delivery_status).color,
                          fontWeight: 600,
                          height: { xs: 20, sm: 24 },
                          fontSize: { xs: "9px", sm: "11px" },
                        }}
                      />
                      {orderData?.order_current_status_message &&
                       orderData?.order_current_status_message !== orderData?.delivery_status && (
                        <Typography
                          sx={{
                            fontSize: { xs: "10px", sm: "12px" },
                            fontWeight: 500,
                            color: "#6B7280",
                          }}
                        >
                          {orderData.order_current_status_message}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
                {/* <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ 
                    fontSize: { xs: "12px", sm: "13px" }, 
                    color: "#111827" 
                  }}>
                    {orderData.invoice_number || orderData.job_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    fontSize: { xs: "10px", sm: "11px" } 
                  }}>
                    Job No: {orderData.job_id}
                  </Typography>
                </Box> */}
              </Box>

              <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
                {/* Driver / Stops Completed / Orders Delivered summary -
                    shown in place of the Drops/References/Lines chip above,
                    controlled by the API's is_driver_search flag */}
                {isDriverSearch && (() => {
                  const [completedStops, totalStops] = (orderData?.total_stops || "0/0")
                    .split("/")
                    .map((n) => Number(n) || 0);
                  const [deliveredOrders, totalOrders] = (orderData?.total_orders || "0/0")
                    .split("/")
                    .map((n) => Number(n) || 0);

                  return (
                    <TrackingDriverSummary
                      data={{
                        driverName: orderData?.assigned_driver || "-",
                        completedStops,
                        totalStops,
                        deliveredOrders,
                        totalOrders,
                      }}
                    />
                  );
                })()}

                {/* Route Progress - controlled by the API's is_driver_search flag */}
                {isDriverSearch && orderData?.driver_delivery_current_route_progress?.route_list?.length > 0 && (
                  <TrackingRouteProgress
                    currentStopNumber={orderData.driver_delivery_current_route_progress.current_stop}
                    routeList={orderData.driver_delivery_current_route_progress.route_list}
                  />
                )}

                {/* Delivery Status History Table */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1.5, 
                      color: "#111827",
                      fontSize: { xs: "14px", sm: "15px" },
                    }}
                  >
                    Related Job Details Status
                  </Typography>

                  <TableContainer
                    component={Paper}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      boxShadow: "none",
                      overflow: "auto",
                      backgroundColor: "#FFFFFF",
                      maxHeight: getTableMaxHeight(),
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#F1F1F1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#C1C7CD',
                        borderRadius: '4px',
                        '&:hover': {
                          background: '#A0A8B0',
                        },
                      },
                    }}
                  >
                    <Table 
                      size="small"
                      sx={{
                        minWidth: { xs: '700px', sm: '800px', md: 'auto' },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                          backgroundColor: "#F9FAFB",
                          borderBottom: "1px solid #E5E7EB",
                          color: "#6B7280",
                          fontWeight: 600,
                          fontSize: { xs: "10px", sm: "12px" },
                          py: { xs: 1, sm: 1.5 },
                          whiteSpace: 'nowrap',
                        },
                        '& .MuiTableBody-root .MuiTableCell-root': {
                          borderBottom: "1px solid #F3F4F6",
                          color: "#111827",
                          fontSize: { xs: "11px", sm: "13px" },
                          py: { xs: 0.75, sm: 1.25 },
                          whiteSpace: 'nowrap',
                        },
                        '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {/* ✅ Job No - Sticky Left ONLY */}
                          <TableCell 
                            sx={{ 
                              fontWeight: 700,
                              position: 'sticky',
                              left: 0,
                              zIndex: 3,
                              backgroundColor: "#F9FAFB",
                              minWidth: '100px',
                            }}
                          >
                            Job No
                          </TableCell>
                          {isDriverSearch && (
                            <TableCell sx={{ fontWeight: 700, minWidth: '90px' }}>Stop No</TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 700, minWidth: '100px' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, minWidth: '150px' }}>Date / Time</TableCell>
                          {!isDriverSearch && (
                            <TableCell sx={{ fontWeight: 700, minWidth: '120px' }}>Driver</TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 700, minWidth: '150px' }}>Remarks</TableCell>
                          <TableCell sx={{ fontWeight: 700, minWidth: '100px', textAlign: "center" }}>Signature</TableCell>
                          {/* Files column - NOT sticky anymore */}
                          <TableCell sx={{ fontWeight: 700, textAlign: "center", minWidth: '100px' }}>
                            Files
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {deliveryStatusHistory.length > 0 ? (
                          deliveryStatusHistory.map((history, index) => {
                            const signatureFiles = history?.signature_files || [];
                            const otherFiles = history?.other_files || [];
                            const isSelected = index === selectedHistoryIndex;
                            // Solid (non-translucent) colors, not rgba - the sticky Job No
                            // cell paints its own background on top of the row's background,
                            // so two stacked translucent layers there would render darker
                            // than the single translucent layer other columns show through.
                            // Using solid hex for both keeps every column identical.
                            const rowBaseColor = isSelected ? "#F1EDFC" : "#FFFFFF";
                            const rowHoverColor = isSelected ? "#E8E1FA" : "#F5F5F5";

                            return (
                              <TableRow
                                key={`${history?.date_time || "status"}-${index}`}
                                onClick={() => setSelectedHistoryIndex(index)}
                                sx={{
                                  cursor: "pointer",
                                  backgroundColor: rowBaseColor,
                                  "&:hover": {
                                    backgroundColor: rowHoverColor,
                                  },
                                  "&:hover .sticky-job-no-cell": {
                                    backgroundColor: rowHoverColor,
                                  },
                                }}
                              >
                                {/* ✅ Job No - Sticky Left ONLY */}
                                <TableCell
                                  className="sticky-job-no-cell"
                                  sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 2,
                                    backgroundColor: rowBaseColor,
                                  }}
                                >
                                  {history?.job_no || "-"}
                                </TableCell>
                                {isDriverSearch && (
                                  <TableCell>{history?.stop_number ?? "-"}</TableCell>
                                )}
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={history?.status || "-"}
                                    sx={{
                                      backgroundColor: history?.status_color || getStatusColor(history?.status).bgcolor,
                                      color: history?.status_color ? "#fff" : getStatusColor(history?.status).color,
                                      border: "none",
                                      fontWeight: 600,
                                      height: { xs: 20, sm: 24 },
                                      fontSize: { xs: "9px", sm: "11px" },
                                      '& .MuiChip-label': {
                                        px: { xs: 0.75, sm: 1 },
                                      },
                                    }}
                                  />
                                </TableCell>
                                <TableCell>{formatDateTime(history?.date_time)}</TableCell>
                                {!isDriverSearch && (
                                  <TableCell>{history?.driver || "-"}</TableCell>
                                )}
                                <TableCell sx={{ maxWidth: { xs: 150, sm: 200, md: 250 } }}>
                                  <Tooltip title={history?.remarks || history?.actionMessage || "-"}>
                                    <span style={{
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {history?.remarks || history?.actionMessage || "-"}
                                    </span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  {signatureFiles.length > 0 ? (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() =>
                                        handleOpenFileViewer(signatureFiles, "Signature")
                                      }
                                      sx={{ 
                                        textTransform: "none",
                                        fontSize: { xs: "10px", sm: "12px" },
                                        borderColor: "#D1D5DB",
                                        color: "#374151",
                                        padding: { xs: '2px 6px', sm: '4px 10px' },
                                        minWidth: { xs: '40px', sm: 'auto' },
                                        '&:hover': {
                                          borderColor: "#7152F3",
                                          color: "#7152F3",
                                          backgroundColor: "rgba(113, 82, 243, 0.04)",
                                        },
                                      }}
                                    >
                                      View
                                    </Button>
                                  ) : (
                                    "-"
                                  )}
                                </TableCell>
                                {/* Files column - NOT sticky anymore */}
                                <TableCell sx={{ textAlign: "center" }}>
                                  {otherFiles.length > 0 ? (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() =>
                                        handleOpenFileViewer(otherFiles, "Other Files")
                                      }
                                      sx={{ 
                                        textTransform: "none",
                                        fontSize: { xs: "10px", sm: "12px" },
                                        borderColor: "#D1D5DB",
                                        color: "#374151",
                                        padding: { xs: '2px 6px', sm: '4px 10px' },
                                        minWidth: { xs: '40px', sm: 'auto' },
                                        '&:hover': {
                                          borderColor: "#7152F3",
                                          color: "#7152F3",
                                          backgroundColor: "rgba(113, 82, 243, 0.04)",
                                        },
                                      }}
                                    >
                                      {otherFiles.length > 1 ? `View (${otherFiles.length})` : "View"}
                                    </Button>
                                  ) : (
                                    "-"
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 3, color: "#6B7280" }}>
                              No delivery status history available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Order Information - Invoice Details (reflects the
                    row selected in Related Job Details Status above) */}
                <OrderInformation orderDetails={selectedDetails} />

                {/* Order Lines Table */}
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1.5, 
                      color: "#111827",
                      fontSize: { xs: "14px", sm: "15px" },
                    }}
                  >
                    Items in Order
                  </Typography>

                  <TableContainer
                    component={Paper}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      boxShadow: "none",
                      overflow: "auto",
                      backgroundColor: "#FFFFFF",
                      maxHeight: isMobile ? '300px' : '400px',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#F1F1F1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#C1C7CD',
                        borderRadius: '4px',
                        '&:hover': {
                          background: '#A0A8B0',
                        },
                      },
                    }}
                  >
                    <Table 
                      size="small"
                      sx={{
                        minWidth: { xs: '450px', sm: '550px', md: 'auto' },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                          backgroundColor: "#F9FAFB",
                          borderBottom: "1px solid #E5E7EB",
                          color: "#6B7280",
                          fontWeight: 600,
                          fontSize: { xs: "10px", sm: "12px" },
                          py: { xs: 1, sm: 1.5 },
                          whiteSpace: 'nowrap',
                        },
                        '& .MuiTableBody-root .MuiTableCell-root': {
                          borderBottom: "1px solid #F3F4F6",
                          color: "#111827",
                          fontSize: { xs: "11px", sm: "13px" },
                          py: { xs: 0.75, sm: 1.25 },
                        },
                        '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {/* ✅ Stock Code - Sticky Left ONLY */}
                          <TableCell 
                            sx={{ 
                              fontWeight: 700,
                              position: 'sticky',
                              left: 0,
                              zIndex: 3,
                              backgroundColor: "#F9FAFB",
                              minWidth: '100px',
                            }}
                          >
                            Stock Code
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, minWidth: '180px' }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 700, minWidth: '80px' }} align="right">Item Quantity</TableCell>
                          {/* ❌ Location column removed */}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDetails?.orderLines?.map((line, index) => (
                          <TableRow key={index} hover>
                            {/* ✅ Stock Code - Sticky Left ONLY */}
                            <TableCell 
                              sx={{ 
                                position: 'sticky',
                                left: 0,
                                zIndex: 2,
                                backgroundColor: "#FFFFFF",
                                fontWeight: 500,
                              }}
                            >
                              {line.stockCode || line.stock_code}
                            </TableCell>
                            <TableCell sx={{ maxWidth: { xs: 150, sm: 200, md: 300 } }}>
                              <Tooltip title={line.stock_description}>
                                <span style={{ 
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {line.stock_description}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 500 }}>
                              {line.order_qty}
                            </TableCell>
                            {/* ❌ Location column removed */}
                          </TableRow>
                        ))}
                        {!selectedDetails?.orderLines?.length && (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: "#6B7280" }}>
                              No items available for this invoice.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Container>

      <Box sx={{ py: { xs: 4, sm: 5, md: 6 }, textAlign: "center", opacity: 0.5 }}>
        <Typography variant="body2" sx={{ color: "#6B7280", fontSize: { xs: "10px", sm: "12px" } }}>
          © {new Date().getFullYear()} SSS Auto Tracking Systems
        </Typography>
      </Box>

      <ScrollToTopButton show={showScrollTop} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />

      {/* File Viewer Dialog */}
      <Dialog
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.95)",
            borderRadius: "12px",
            overflow: "hidden",
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            px: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "14px", sm: "16px" } }}>
            {viewerTitle}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Copy image">
              <IconButton onClick={handleCopyImage} size="small" sx={{ color: "#fff" }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download image">
              <IconButton onClick={handleDownloadImage} size="small" sx={{ color: "#fff" }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleCloseFileViewer} size="small" sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: { xs: "300px", sm: "420px" },
          }}
        >
          {viewerFiles.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevFile}
                sx={{
                  position: "absolute",
                  left: { xs: 8, sm: 16 },
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  '&:hover': {
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  },
                  zIndex: 1,
                  size: { xs: 'small', sm: 'medium' },
                }}
              >
                <ChevronLeftIcon fontSize={isMobile ? "medium" : "large"} />
              </IconButton>

              <IconButton
                onClick={handleNextFile}
                sx={{
                  position: "absolute",
                  right: { xs: 8, sm: 16 },
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  '&:hover': {
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  },
                  zIndex: 1,
                }}
              >
                <ChevronRightIcon fontSize={isMobile ? "medium" : "large"} />
              </IconButton>
            </>
          )}

          <Box
            sx={{
              width: "100%",
              px: { xs: 4, sm: 8 },
              py: { xs: 1, sm: 2 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {viewerFiles.length > 0 && (
              <img
                src={getFilePath(viewerFiles[selectedFileIndex])}
                alt={viewerFiles[selectedFileIndex]?.file_name || "Delivery file"}
                style={{
                  maxWidth: "100%",
                  maxHeight: { xs: "50vh", sm: "75vh" },
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{ 
                color: "rgba(255, 255, 255, 0.75)", 
                mt: 2, 
                textAlign: "center",
                fontSize: { xs: "11px", sm: "13px" },
                px: 1,
              }}
            >
              {viewerFiles.length > 0
                ? `${viewerFiles[selectedFileIndex]?.file_name || "File"} (${selectedFileIndex + 1} of ${viewerFiles.length})`
                : "No file available"}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={viewerSnackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseViewerSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseViewerSnackbar} severity="success" sx={{ width: "100%" }}>
          {viewerSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublicTracking;